import { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronsUp, Loader2, Lock, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { prioridadesAPI, formatApiError } from '@/lib/api';
import { fechaYHora, soloFecha } from '../../lib/fechas';

/**
 * La lista de prioridad de la empresa: Dirección intercala casos de todas las gerencias.
 *
 * **Una gran bolsa, con una sola regla (2026-10-09).** Dirección pone un caso de Producción antes
 * que uno de Administración, en el orden que quiera, **siempre que no invierta el orden que dejó el
 * responsable de cada gerencia**. Por eso una flecha que invertiría dos casos de la misma gerencia,
 * ordenados por su responsable, está deshabilitada y dice quién ordena ahí. El servidor aplica la
 * misma regla: esta pantalla no ofrece lo que él rechazaría.
 */

/** Si mover el caso `i` a `j` invierte dos casos de la misma gerencia ordenados por su responsable. */
function invierte(lista, i, j) {
  const caso = lista[i];
  if (caso.posicion_interna == null || !caso.requesting_department) return null;
  const [desde, hasta] = j < i ? [j, i - 1] : [i + 1, j];
  for (let k = desde; k <= hasta; k += 1) {
    const otro = lista[k];
    if (otro.requesting_department !== caso.requesting_department || otro.posicion_interna == null) continue;
    const subeSobreUnoAnterior = j < i && otro.posicion_interna < caso.posicion_interna;
    const bajaBajoUnoPosterior = j > i && otro.posicion_interna > caso.posicion_interna;
    if (subeSobreUnoAnterior || bajaBajoUnoPosterior) {
      return `El orden dentro de ${caso.gerencia} lo define ${caso.ordena}: «${(subeSobreUnoAnterior ? otro : caso).title || ''}» va antes que «${(subeSobreUnoAnterior ? caso : otro).title || ''}».`;
    }
  }
  return null;
}

/** Hasta dónde puede subir el caso `i` sin pasar a uno de su gerencia que va antes. */
function topePermitido(lista, i) {
  for (let j = 0; j < i; j += 1) if (!invierte(lista, i, j)) return j;
  return i;
}

export default function PrioridadDeLaEmpresa({ tenantId, capsuleId }) {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);

  const cargar = useCallback(async () => {
    if (!tenantId || !capsuleId) return;
    setCargando(true);
    try {
      const res = await prioridadesAPI.get(tenantId, capsuleId);
      setDatos(res.data);
    } catch (e) {
      toast.error(formatApiError(e) || 'No se pudo cargar la prioridad de la empresa');
    } finally {
      setCargando(false);
    }
  }, [tenantId, capsuleId]);

  useEffect(() => { cargar(); }, [cargar]);

  if (!capsuleId) return <p className="text-xs text-zinc-400 py-8 text-center">Elegí una cápsula arriba.</p>;
  if (cargando && !datos) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  }
  if (!datos) return null;

  const casos = datos.casos || [];
  const puedo = !!datos.puedo_ordenar;

  // **Se guarda hasta el último caso priorizado, no la lista entera** (corrección del 2026-10-09).
  // Guardar todo lo que se ve congelaba a los casos que Dirección nunca tocó en el orden por fecha
  // de ese momento: dejaban de reordenarse cuando cambiaban sus fechas, y la franja «el resto»
  // desaparecía. Lo que Dirección no ubicó sigue detrás, por fecha comprometida.
  const mover = async (i, j) => {
    if (j < 0 || j >= casos.length || i === j) return;
    const aviso = invierte(casos, i, j);
    if (aviso) { toast.error(aviso); return; }
    const copia = [...casos];
    const [c] = copia.splice(i, 1);
    copia.splice(j, 0, { ...c, priorizado: true });
    setDatos((d) => ({ ...d, casos: copia }));
    let ultimo = -1;
    copia.forEach((x, k) => { if (x.priorizado) ultimo = k; });
    try {
      await prioridadesAPI.ordenar(tenantId, capsuleId, copia.slice(0, ultimo + 1).map((x) => x.ticket_id));
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e) || 'No se pudo guardar el orden');
      cargar();
    }
  };

  // **Sacar un caso de la lista de Dirección** (2026-10-14). Antes solo se podía mover: un caso
  // ubicado por error seguía ocupando un lugar de su gerencia. Se guarda la lista sin él, con el
  // mismo endpoint, y queda en el historial. Una sublista de una lista válida no puede invertir el
  // orden de ninguna gerencia, así que el servidor no tiene nada que rechazar.
  const quitar = async (ticketId) => {
    const resto = casos.filter((x) => x.ubicado_por_direccion && x.ticket_id !== ticketId).map((x) => x.ticket_id);
    try {
      await prioridadesAPI.ordenar(tenantId, capsuleId, resto);
      toast.success('Quitado de la lista de Dirección');
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e) || 'No se pudo quitar');
      cargar();
    }
  };

  return (
    <div className="space-y-3" data-testid="prioridad-de-la-empresa">
      <div className="text-xs text-zinc-600 space-y-1">
        <p>
          <b>La prioridad de la empresa.</b> Dirección ordena los casos de todas las gerencias en una
          sola lista. Lo único que no puede hacer es invertir el orden que el responsable de cada
          gerencia les dio a sus casos.
        </p>
        <p className="text-[11px] text-zinc-500">
          Quien planifica el trabajo ve esta lista en la bandeja de Casos, eligiendo el orden por prioridad.
          {datos.actualizado?.at && <> Último cambio: {fechaYHora(datos.actualizado.at)} por {datos.actualizado.actor}.</>}
        </p>
        {!puedo && (
          <p className="text-[11px] text-amber-700 flex items-center gap-1" data-testid="sin-permiso-de-ordenar">
            <Lock className="w-3 h-3" /> Armar esta lista es de Dirección. Podés verla, no cambiarla.
          </p>
        )}
      </div>

      <Card className="rounded-sm border-brand-border">
        <CardContent className="p-0">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-brand-border text-left text-[10px] uppercase tracking-wider text-zinc-500">
                {puedo && <th className="py-2 px-2 w-24">Orden</th>}
                <th className="py-2 px-2 w-8">#</th>
                <th className="py-2 px-2">Caso</th>
                <th className="py-2 px-2">Gerencia</th>
                <th className="py-2 px-2">En su gerencia</th>
                <th className="py-2 px-2">Fecha comprometida</th>
              </tr>
            </thead>
            <tbody>
              {casos.map((c, i) => {
                const bloqueoArriba = i > 0 ? invierte(casos, i, i - 1) : null;
                const bloqueoAbajo = i < casos.length - 1 ? invierte(casos, i, i + 1) : null;
                const tope = topePermitido(casos, i);
                return (
                  <tr key={c.ticket_id} className={`border-b border-zinc-50 ${c.priorizado ? '' : 'text-zinc-500'}`} data-testid={`prioridad-${c.ticket_id}`}>
                    {puedo && (
                      <td className="py-1.5 px-2">
                        <div className="flex items-center gap-0.5">
                          <button type="button" disabled={i === 0 || !!bloqueoArriba}
                            title={bloqueoArriba || 'Subir'} data-testid={`subir-${c.ticket_id}`}
                            onClick={() => mover(i, i - 1)}
                            className="p-0.5 text-zinc-400 hover:text-brand-primary disabled:opacity-25">
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button type="button" disabled={i === casos.length - 1 || !!bloqueoAbajo}
                            title={bloqueoAbajo || 'Bajar'} data-testid={`bajar-${c.ticket_id}`}
                            onClick={() => mover(i, i + 1)}
                            className="p-0.5 text-zinc-400 hover:text-brand-primary disabled:opacity-25">
                            <ArrowDown className="w-3 h-3" />
                          </button>
                          <button type="button" disabled={tope === i}
                            title={tope === i ? 'No puede subir más sin pasar a uno de su gerencia que va antes' : 'Subir todo lo posible'}
                            data-testid={`al-tope-${c.ticket_id}`} onClick={() => mover(i, tope)}
                            className="p-0.5 text-zinc-400 hover:text-brand-primary disabled:opacity-25">
                            <ChevronsUp className="w-3 h-3" />
                          </button>
                          {c.ubicado_por_direccion && (
                            <button type="button"
                              title={c.posicion_interna != null
                                ? `Quitar de la lista de Dirección. Sigue priorizado detrás, porque lo ordenó ${c.ordena}.`
                                : 'Quitar de la lista de Dirección: vuelve al resto, por fecha comprometida.'}
                              data-testid={`quitar-${c.ticket_id}`} onClick={() => quitar(c.ticket_id)}
                              className="p-0.5 ml-1 text-zinc-400 hover:text-red-600">
                              <X className="w-3 h-3" />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                    <td className="py-1.5 px-2 tabular-nums text-zinc-400">{c.priorizado ? i + 1 : '—'}</td>
                    <td className="py-1.5 px-2">
                      <span className="font-medium text-zinc-800">{c.title || c.ticket_id}</span>
                      <span className="block text-[10px] text-zinc-400">{c.ticket_id} · {c.solicitante?.nombre || c.solicitante?.email || ''}</span>
                    </td>
                    <td className="py-1.5 px-2">
                      {c.gerencia ? <Badge variant="outline" className="text-[10px] rounded-sm">{c.gerencia}</Badge> : <span className="text-zinc-400">sin gerencia</span>}
                    </td>
                    <td className="py-1.5 px-2 text-[11px]">
                      {c.posicion_interna != null
                        ? <span className="flex items-center gap-1 text-zinc-600"><Lock className="w-3 h-3" /> ordenado por {c.ordena}</span>
                        : <span className="text-zinc-400">sin ordenar</span>}
                    </td>
                    <td className="py-1.5 px-2 tabular-nums text-zinc-600">{c.fecha_hasta ? soloFecha(c.fecha_hasta) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
